import { Box } from '@mui/material';
import React, { useRef, useState } from 'react';
import PasteReplaceDialog from './PasteReplaceDialog';
import TableColumnGutter from './TableColumnGutter';
import TableGridRow from './TableGridRow';
import { cellText } from './tableModel';
import type { useTableEditor } from './useTableEditor';

type Editor = ReturnType<typeof useTableEditor>;

interface TableGridProps {
  editor: Editor;
  onImportImage: (blob: Blob) => void;
}

/** Model-driven table grid — renders from `editor.anchorsByRow`, edits via `editor` ops. */
export default function TableGrid({ editor, onImportImage }: TableGridProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [pendingPaste, setPendingPaste] = useState<{ html: string; text: string } | null>(null);
  const { anchorsByRow, rowCount } = editor;

  const flatAnchors = anchorsByRow.flatMap((row) => row.map((a) => [a.r, a.c] as const));
  const hasContent = anchorsByRow.some((row) =>
    row.some((a) => cellText(a.cell).trim().length > 0),
  );

  const focusCell = (r: number, c: number) => {
    tableRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${r}-${c}"] [role="textbox"]`)
      ?.focus();
  };

  const step = (r: number, c: number, delta: number) => {
    const idx = flatAnchors.findIndex(([ar, ac]) => ar === r && ac === c);
    const next = flatAnchors[idx + delta];
    if (next) {
      focusCell(next[0], next[1]);
    } else if (delta > 0) {
      editor.addRow(rowCount);
      setTimeout(() => focusCell(rowCount, 0), 0);
    }
  };

  const handlePaste = (html: string, text: string): boolean => {
    if (hasContent) {
      setPendingPaste({ html, text });
      return true;
    }
    return editor.replaceFromClipboard(html, text);
  };

  return (
    <Box sx={{ overflowX: 'auto', '&:hover .table-line-grip': { opacity: 1 } }}>
      <Box component="table" ref={tableRef} sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
        <tbody>
          <TableColumnGutter editor={editor} />
          {anchorsByRow.map((rowAnchors, r) => (
            <TableGridRow
              key={r}
              editor={editor}
              r={r}
              rowAnchors={rowAnchors}
              onPasteTable={handlePaste}
              onPasteImage={onImportImage}
              onStep={step}
            />
          ))}
        </tbody>
      </Box>
      <PasteReplaceDialog
        open={pendingPaste !== null}
        onConfirm={() => {
          if (pendingPaste) editor.replaceFromClipboard(pendingPaste.html, pendingPaste.text);
          setPendingPaste(null);
        }}
        onCancel={() => setPendingPaste(null)}
      />
    </Box>
  );
}
